import { useState } from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { ContentBox } from '@/components/shared/content-box';
import { KeyValue } from '@/components/shared/key-value';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { PageSkeleton } from '@/components/shared/page-skeleton';
import { SupplierType, type History } from '@/data/subsidy/interfaces';
import { useRefundRequestShow } from '@/data/subsidy/refund-requests';
import { RefundStatus } from '@/models/subsidy/status';
import { paymentTypes } from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/steps/invoice-form/constants';
import { PaymentReceiptsTypes } from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/steps/invoice-form/interfaces';
import { ConfirmRefundRequestApproveDialog } from '@/packages/subsidy-management/components/confirm-refund-request-dialog';
import { formatDate } from '@/utils/format-date';
import { formatMoney } from '@/utils/format-money';

const keyWidth = 200;

const findApprovedHistory = (history: History[]) => {
  const approvedItem = history?.find((item) => item.approved === true);
  return approvedItem
    ? { message: approvedItem.message, approved: approvedItem.approved }
    : null;
};

const ApprovedMessages = ({ history }: { history: History[] }) => {
  return (
    <Stack direction={{ md: 'row' }} gap={1}>
      <Typography fontWeight={fontWeights.fontWeightSemibold} width={keyWidth}>
        Validação de comprovantes
      </Typography>
      <Stack>
        {history?.length > 0 ? (
          history.map((item) => (
            <Box key={item.message}>
              <Typography display="block">{item.message}</Typography>
              <Typography display="block">{item.decision_comment}</Typography>
            </Box>
          ))
        ) : (
          <Typography>Nenhum histórico disponível</Typography>
        )}
      </Stack>
    </Stack>
  );
};

export const RefundRequestDetails = () => {
  const navigate = useNavigate();
  const [openRefundRequestDialog, setOpenRefundRequestDialog] = useState(false);
  const { refund_request_uuid } = useParams();
  const { refundRequest, isLoading } = useRefundRequestShow(
    refund_request_uuid || ''
  );
  const receipts = refundRequest?.receipts ?? [];
  const purchaseReceipts = receipts.find(
    (r) => !paymentTypes.includes(r.type as PaymentReceiptsTypes)
  );
  const paymentReceipts = receipts.find(
    (r) =>
      r.type === PaymentReceiptsTypes.Payment ||
      r.type === PaymentReceiptsTypes.BillingStatement
  );
  const othersReceipts = receipts.find(
    (r) => r.type === PaymentReceiptsTypes.Other
  );

  const canOverrideRefundDecision = () => {
    const enable_override_refund_decision = refundRequest?.policies.some(
      (policy) => policy.enable_override_refund_decision
    );

    const allowedStatus = [
      RefundStatus.RefundRequestPendingApproval,
      RefundStatus.RefundRequestRevisionRequired,
      RefundStatus.RefundRequestRejected,
    ];

    return (
      enable_override_refund_decision &&
      !!refundRequest?.status &&
      allowedStatus.includes(refundRequest.status)
    );
  };

  if (isLoading) return <PageSkeleton />;

  return (
    <PageContainer>
      <PageHeader
        backNavigationText="Detalhes do reembolso"
        handleBackNavigation={() => navigate(-1)}
        actions={
          canOverrideRefundDecision()
            ? [
                {
                  label: 'Aprovar reembolso',
                  onClick: () => {
                    setOpenRefundRequestDialog(true);
                  },
                },
              ]
            : undefined
        }
      />

      <ContentBox gap={2}>
        <Typography mb={1} variant="h2" color="primary.dark">
          Detalhes do pedido
        </Typography>
        <KeyValue
          label="Data do pagamento"
          value={formatDate(refundRequest?.payment_date)}
        />
        <Stack direction={{ md: 'row' }} spacing={2}>
          <Typography
            fontWeight={fontWeights.fontWeightSemibold}
            width={keyWidth}
          >
            Comprovante de pagamento
          </Typography>

          {paymentReceipts?.attachments?.map((item) => (
            <Link
              key={item.url}
              href={item?.url}
              target="_blank"
              rel="noopener"
              margin="0 8px !important"
            >
              {item.content.filename}
            </Link>
          ))}
        </Stack>
        <KeyValue
          label={
            refundRequest?.reference_month
              ? 'Valor da mensalidade'
              : 'Valor da compra'
          }
          value={formatMoney(refundRequest?.purchase_amount || '')}
        />
        {refundRequest?.reference_month && (
          <KeyValue
            label="Mês de referência"
            value={String(refundRequest?.reference_month) || ''}
          />
        )}
        <KeyValue
          label="Nome do fornecedor"
          value={refundRequest?.supplier_name || ''}
        />
        {refundRequest?.supplier_type && (
          <KeyValue
            label={
              refundRequest?.supplier_type === SupplierType.LegalPerson
                ? 'CNPJ'
                : 'CPF'
            }
            value={refundRequest?.supplier_document || ''}
          />
        )}
        <KeyValue
          label="Valor do pagamento"
          value={formatMoney(refundRequest?.payment_amount || '')}
        />
        <KeyValue
          label="Data da compra"
          value={formatDate(refundRequest?.purchase_date || '')}
        />
        <Stack direction={{ md: 'row' }} gap={{ md: 2 }}>
          <Typography
            fontWeight={fontWeights.fontWeightSemibold}
            width={keyWidth}
          >
            Comprovante de compra
          </Typography>
          {purchaseReceipts?.attachments?.map((item) => (
            <Link
              key={item.url}
              href={item.url}
              target="_blank"
              rel="noopener"
              margin={{ md: '0 -8px !important' }}
            >
              {item.content.filename}
            </Link>
          ))}
        </Stack>
        {refundRequest?.banking_info?.pix_key && (
          <KeyValue
            label="Chave PIX"
            value={refundRequest?.banking_info?.pix_key || ''}
          />
        )}
        {refundRequest?.banking_info?.bank && (
          <>
            <KeyValue
              label="Número da conta"
              value={refundRequest?.banking_info?.account_number || ''}
            />
            <KeyValue
              label="Banco"
              value={refundRequest?.banking_info?.bank || ''}
            />
            <KeyValue
              label="Agência"
              value={refundRequest?.banking_info?.agency || ''}
            />
          </>
        )}
        {othersReceipts && (
          <Stack direction={{ md: 'row' }} gap={{ md: 2 }}>
            <Typography
              fontWeight={fontWeights.fontWeightSemibold}
              width={keyWidth}
            >
              Outros comprovantes
            </Typography>
            {othersReceipts?.attachments?.map((item) => (
              <Link
                key={item.url}
                href={item?.url}
                target="_blank"
                rel="noopener"
                margin={{ md: '0 -8px !important' }}
              >
                {item.content.filename}
              </Link>
            ))}
          </Stack>
        )}
      </ContentBox>
      <ContentBox gap={2} mt={2}>
        <Typography mb={1} variant="h2" color="primary.dark">
          Histórico
        </Typography>
        <KeyValue
          label="Solicitação do item"
          value={formatDate(refundRequest?.created_at) || '-'}
        />
        <KeyValue
          label="Envio de comprovantes"
          value={formatDate(refundRequest?.created_at) || '-'}
        />
        <KeyValue
          label="Aprovação do item"
          value={
            findApprovedHistory(refundRequest?.history || [])?.message || '-'
          }
        />
        <ApprovedMessages history={refundRequest?.history || []} />
      </ContentBox>
      <ConfirmRefundRequestApproveDialog
        onClose={() => setOpenRefundRequestDialog(false)}
        open={openRefundRequestDialog}
      />
    </PageContainer>
  );
};
